import { Agency } from "./Agency";
import { AgencyMember } from "./AgencyMember";
import { Allergy } from "./Allergy";
import { Caregiver, CaregiverWithReason } from "./Caregiver";
import { Geo } from "./Geo";
import { DayOfWeek, Duration, Instant, LocalDate, LocalDateTime, LocalTime } from "./JSJodaJSON";
import {
  AgencyMemberId,
  CaregiverCertification,
  CaregiverId,
  ClusterId,
  ImportedVisitId,
  OnboardingVisitId,
  OnboardingVisitInstanceId,
  PatientId,
  ScheduledVisitId,
  VisitBroadcastId,
  VisitInstanceId,
} from "./ObjectIds";
import { PatientAdvancedDirective } from "./Patient";
import { BroadcastPattern } from "./VisitBroadcast";

export type PatientGender = "F" | "M" | null;

export interface UnconfirmedVisits {
  id: VisitBroadcastId;
  createdAt: Instant;
  startTime: LocalDateTime;
  endTime: LocalDateTime;

  patientName: string;
  patientAddress: string;
  patientAddressGeoLocation: Geo;
  caregiver: CaregiverId;
  isRecurring: boolean;
  visitsDetails: UnconfirmedVisitsDetails[];
}

export interface UnconfirmedVisitsDetails {
  scheduledId: ScheduledVisitId;
  scheduledStartTime: LocalDateTime;
  scheduledEndTime: LocalDateTime;
  checkinLocation: ScheduledVisitLocation | null;
  checkoutLocation: ScheduledVisitLocation | null;
  caregiverVisitLocation: CaregiverLocationPing[] | null;
  checkinTimeUTC: Instant | null;
  checkoutTimeUTC: Instant | null;
  checkinTimeLocal: LocalDateTime | null;
  checkoutTimeLocal: LocalDateTime | null;
  isSummarySubmitted: boolean | null;
}

export interface CheckedInVisitResponse {
  scheduledId: ScheduledVisitId | null;
}

export interface CaregiverLocationPing {
  sentTime: Instant;
  location: Geo;
}

export interface UnconfirmedVisitsList {
  visits: UnconfirmedVisits[];
}

export interface VisitStaffingPreferences {
  smoking: boolean | null;
  bedbound: boolean | null;
  cane: boolean | null;
  walker: boolean | null;
  wheelchair: boolean | null;
  kosher: boolean | null;
  halal: boolean | null;
  hoyer: boolean | null;
  pets: boolean | null;
}

export interface PublicVisitInstanceDetails {
  createdAt: Instant;

  agency: Agency;
  isBillingAgency: boolean;
  isMedflytAgency: boolean;

  startTime: Instant;
  startTimeLocal: LocalDateTime;

  endTime?: Instant;
  endTimeLocal?: LocalDateTime;

  /**
   * Is this a single visit or recurring visit?
   */
  isSingleVisit: boolean;

  /**
   * Defines the time slots of the visit over a given week.
   */
  dayTimeParams: VisitDayTimeParams | null;

  /**
   * USD, integer cents. "$10.99" equals the value 1099
   *
   * @exclusiveMinimum 0
   */
  price: number | null;

  /**
   * Must be one of 'Caregiver.ALL_LANGUAGES'
   */
  languages: string[];

  generalNotes: string;

  location: AddressComponents;

  /**
   * timezone from the tz database (IANA)
   */
  timezone: string;

  createdById: AgencyMemberId;
  createdByFirstName: string;

  createdByLastName: string;

  flexibleVisitParams: FlexibleVisitParams | null;

  visitLabels: VisitLabel[];

  patientId: PatientId;
  patientInitials: string;
  patientAge: number | null;
  patientGender: PatientGender | null;
  patientAllergies: Allergy[];
  patientLanguages: string[];
  patientRequests: string[];
  patientMedicalNotes: string | null;
  patientTasks: string[];
  certifications: CaregiverCertification[];
  clockInToleranceMinutes: number;
  clockOutToleranceMinutes: number;

  staffingPreferences2: VisitStaffingPreferences;
  patientAdvancedDirectives: PatientAdvancedDirective | null;
}

export interface VisitDocumentsStatus {
  total: number;
  submitted: number;
}

export interface VisitInstanceDetails {
  id: VisitInstanceId;
  startTimeUTC: Instant;
  startTimeLocal: LocalDateTime;
  endTimeUTC: Instant;
  endTimeLocal: LocalDateTime;
  /**
   * @deprecated Use [[clockInTime]]
   */
  checkInTime: Instant | null;

  /**
   * @deprecated Use [[clockOutTime]]
   */
  checkOutTime: Instant | null;

  clockInTime: LocalDateTime | null;
  clockOutTime: LocalDateTime | null;
  dutySheetFillPercentage: number | null;

  /**
   * Agency pilot for enabled clock in/out caregivers
   */
  isCaregiverClockEnabled: boolean;
  publicVisitDetails: PublicVisitInstanceDetails;
  confidentialVisitDetails: ConfidentialVisitDetails;
  visitDocumentsStatus: VisitDocumentsStatus;
  manualClockTimeEditApproved: boolean;
  isDutySheetCompleted: boolean;
  timeSheetSubmittedAt: Instant | null;
  timeSheetDeclinedAt: Instant | null;
  timeSheetApprovedAt: Instant | null;
  hasPlanOfCare: boolean;
  checkinMaxDistance: number | null;
  requireSelfieOnClockIn: boolean;
  requireSelfieOnClockOut: boolean;
}

export interface VisitInstanceDetailsWithType extends VisitInstanceDetails {
  type: "VisitInstance";
}

/**
 * API payload from server containing all scheduled visits for a given visit
 */
export interface CaregiverAssignedVisits {
  visitInstances: VisitInstanceDetails[];
  importedVisits: CaregiverImportedVisitDetails[];
}

export interface FileStatus {
  visitfileStatus: boolean;
}

/**
 * Exposes information about a visit that is permissible to be viewed by agency members
 */
export interface CoordinatorVisitDetails {
  id: VisitBroadcastId;

  createdAt: Instant;

  updatedAt: Instant;

  createdBy: AgencyMember;

  assignedCoordinator: AgencyMemberId;

  previewedBy: CaregiverWithReason[];

  moreDetailsViewedBy: CaregiverWithReason[];

  acceptedBy: Caregiver[];

  assignedCaregiver: Caregiver | null;

  assignedAt: Instant | null;

  cancelled: boolean;

  cancelledByCaregiver: boolean;

  cancelledReason: string | null;

  startTime: LocalDateTime;

  endTime: LocalDateTime;

  /**
   * Is this a single visit or recurring visit?
   */
  isSingleVisit: boolean;

  /**
   * Defines the time slots of the visit over a given week.
   */
  dayTimeParams: VisitDayTimeParams | null;

  /**
   * USD, integer cents. "$10.99" equals the value 1099
   *
   * @type integer
   * @minimum 0
   * @exclusiveMinimum 0
   */
  price: number | null;

  /**
   * Must be one of 'Caregiver.ALL_CERTIFICATIONS'
   */
  certification: string;

  /**
   * "M" or "F" (or null for either gender)
   */
  gender: string | null;

  /**
   * Must be one of 'Caregiver.ALL_LANGUAGES'
   */
  languages: string[];

  generalNotes: string;

  patientName: string;

  patientAddress: string;
  patientAddressLoc: Geo;

  /**
   * timezone from the tz database (IANA)
   */
  timezone: string;

  /**
   * Value is in meters
   */
  assignableCaregiverRadius: number | null;

  patientPhone: string | null;

  patientAlternativePhone: string | null;

  caseDetails: string;

  salesRep: string | null;

  referralDate: LocalDate | null;

  referralSource: string | null;

  // TODO make not null after every visit has a payment type
  paymentType: string | null;

  hideEndDateFromCaregiver: boolean | null;

  rnVisitParams: RNVisitParams;
}

export interface AgencyVisitsList {
  visits: CoordinatorVisitDetails[];
}

/**
 * for when an agency requests a caregivers visits
 */
export interface AgencyCaregiverAssignedVisit {
  id: VisitBroadcastId;

  patientName: string;

  startTime: LocalDateTime;

  endTime: LocalDateTime;

  /**
   * Is this a single visit or recurring visit?
   */
  isSingleVisit: boolean;

  /**
   * Defines the time slots of the visit over a given week.
   */
  dayTimeParams: VisitDayTimeParams | null;

  /**
   * USD, integer cents. "$10.99" equals the value 1099
   *
   * @type integer
   * @minimum 0
   * @exclusiveMinimum 0
   */
  price: number | null;

  patientAddress: string;
  patientAddressLoc: AddressComponents;

  /**
   * timezone from the tz database (IANA)
   */
  timezone: string;
}

/**
 * Imported Visit for when an agency requests a caregivers visits
 */
export interface AgencyCaregiverImportedVisit {
  startTime: LocalDateTime;

  endTime: LocalDateTime;
}

/**
 * for when an agency requests a caregivers visits
 */
export interface AgencyCaregiverAssignedVisitsList {
  visits: AgencyCaregiverAssignedVisit[];
  importedVisits: AgencyCaregiverImportedVisit[];
}

export interface VisitCreateParams {
  startTime: LocalDateTime;

  endTime: LocalDateTime;

  /**
   * Is this a single visit or recurring visit?
   */
  isSingleVisit: boolean;

  /**
   * Defines the time slots of the visit over a given week.
   */
  dayTimeParams: VisitDayTimeParams | null;
  /**
   * USD, integer cents. "$11.99" equals the value 1199
   *
   * @type integer
   * @minimum 0
   * @exclusiveMinimum 0
   */
  price: number | null;

  /**
   * Must be one of 'Caregiver.ALL_CERTIFICATIONS'
   */
  certification: string;

  /**
   * "M" or "F" (or null for either gender)
   */
  gender: string | null;

  /**
   * Must be one of 'Caregiver.ALL_LANGUAGES'
   */
  languages: string[];

  // TODO Making this optional temporarily because of buggy client
  generalNotes?: string;

  patientName: string;

  patientAddress: string;

  /**
   * Should be derived from the patientAddress using the Google Maps Geocoding API
   */
  patientAddressComponents: AddressComponents;

  /**
   * Value is in meters
   */
  assignableCaregiverRadius: number | null;

  patientPhone: string | null;

  patientAlternativePhone: string | null;

  paymentType: string;

  hideEndDateFromCaregiver: boolean | null;

  // TODO Making this optional temporarily because of buggy client
  caseDetails?: string;

  salesRep?: string | null;

  referralDate?: LocalDate | null;

  referralSource?: string | null;

  /**
   * timezone from the tz database (IANA)
   */
  timezone: string;

  assignedCoordinator: AgencyMemberId;
}

export interface VisitEditParams {
  startTime?: LocalDateTime;

  endTime?: LocalDateTime;

  /**
   * Is this a single visit or recurring visit?
   */
  isSingleVisit: boolean;

  /**
   * Defines the time slots of the visit over a given week.
   */
  dayTimeParams: VisitDayTimeParams | null;

  /**
   * USD, integer cents. "$10.99" equals the value 1099
   *
   * @type integer
   * @minimum 0
   * @exclusiveMinimum 0
   */
  price?: number | null;

  /**
   * Must be one of 'Caregiver.ALL_CERTIFICATIONS'
   */
  certification?: string;

  /**
   * "M" or "F" (or null for either gender)
   */
  gender?: string | null;

  /**
   * Must be one of 'Caregiver.ALL_LANGUAGES'
   */
  languages?: string[];

  generalNotes?: string;

  patientName?: string;

  patientAddress?: string;

  /**
   * timezone from the tz database (IANA)
   */
  timezone?: string;

  /**
   * Should be derived from the patientAddress using the Google Maps Geocoding API
   */
  patientAddressComponents?: AddressComponents;

  /**
   * Value is in meters
   */
  assignableCaregiverRadius?: number | null;

  patientPhone?: string | null;

  patientAlternativePhone?: string | null;

  caseDetails?: string;

  salesRep?: string | null;

  referralDate?: LocalDate | null;

  referralSource?: string | null;

  paymentType?: string;

  hideEndDateFromCaregiver?: boolean | null;

  assignedCoordinator?: AgencyMemberId;
}

// TODO Benny - what is this?
/**
 *
 */
export interface CaregiverGeoLocation {
  sentTime: number;
  location: Geo;
}

export interface ScheduledVisitLocation {
  addressComponents: AddressComponents;
  address: string | null;
}

export interface AddressComponents {
  location: Geo;
  country: string;
  administrativeAreaLevel1: string;
  administrativeAreaLevel2: string;
  sublocalityLevel1?: string | null;
  neighborhood?: string | null;
  locality?: string | null;
  formatedAddressDetails?: FormatedAddressDetails;
}

export interface FormatedAddressDetails {
  location: Geo;
  address1: string;
  country: string;
  state: string;
  county: string;
  fullAddress: string;
  city?: string | null;
  zip5?: string | null;
  zip4?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  crossStreet?: string | null;
  address2?: string | null;
}

export interface VisitDayTimeParams {
  dayTimes: VisitDayTime[];
}

export interface VisitDayTime {
  /**
   * 0 = sunday, 1 = monday, ... , 6 = saturday
   * @TJS-type integer
   * @minimum 0
   * @maximum 6
   */
  day: number;

  startTime: LocalTime;

  endTime: LocalTime;
}

export interface VisitDatesParams {
  startDateTime: LocalDateTime;
  endDateTime: LocalDateTime;
}

export interface VisitDatesParamsExtended {
  visitInstanceId: VisitInstanceId;
  startDateTimeUtc: Instant;
  endDateTimeUtc: Instant;
  startLocalDateTime: LocalDateTime;
  endLocalDateTime: LocalDateTime;
}

export interface CaregiverVisitBroadcastList {
  visitBroadcasts: CaregiverVisitBroadcast[];
}

export type CaregiverVisitBroadcast =
  | CaregiverVisitBroadcastTypes.Available
  | CaregiverVisitBroadcastTypes.Onboarding;

export declare namespace CaregiverVisitBroadcastTypes {
  export interface Available {
    type: "Available";
    id: VisitBroadcastId;
    publicVisitBroadcastDetails: PublicVisitBroadcastDetails;
    caregiverVisitEngagementDetails: CaregiverVisitBroadcastEngagementDetails;
  }
  export interface Onboarding {
    type: "Onboarding";
    id: OnboardingVisitId;
    publicVisitBroadcastDetails: PublicOnboardingVisitDetails;
    caregiverVisitEngagementDetails: CaregiverOnboardingVisitEngagementDetails;
  }
}

export type PublicVisitBroadcastDetails =
  | PublicVisitBroadcastDetailsTypes.Pattern
  | PublicVisitBroadcastDetailsTypes.Singles
  | PublicVisitBroadcastDetailsTypes.FlexiblePattern;

export namespace PublicOnboardingVisitDetailsTypes {
  export interface Pattern extends BaseDetails {
    type: "Pattern";
    weeklyPattern: BroadcastPattern;
  }

  export interface Singles extends BaseDetails {
    type: "Singles";
  }

  interface BaseDetails {
    id: OnboardingVisitId;
    createdAt: Instant;
    agency: Agency;
    startTimeLocal: LocalDateTime;
    endTimeLocal: LocalDateTime;
    languages: string[];
    generalNotes: string;
    location: AddressComponents;
    price: number | null;

    /**
     * timezone from the tz database (IANA)
     */
    timezone: string;
    createdByFirstName: string;
    createdByLastName: string;
    visitLabels: VisitLabelText[];
    patientId: PatientId;
    patientFirstName: string;
    patientIsActive: boolean;
    patientAge: number | null;
    patientInitials: string;
    patientGender: PatientGender | null;
    patientAllergies: Allergy[];
    patientMedicalNotes: string | null;
    patientLanguages: string[];
    patientRequests: string[];
    patientTasks: string[];
    createdById: AgencyMemberId;
    certifications: CaregiverCertification[];
    hideEndDateFromCaregiver: boolean;
    visitInstances: {
      date: VisitDatesParams;
      visitInstanceId: OnboardingVisitInstanceId;
    }[];
    staffingPreferences: VisitStaffingPreferences;
    patientAvailability: PatientVisitTimeFramesByDay[] | null;
    visitInfo: string[] | null;
    nearLocationDisplay: string | null;
  }
}

interface CaregiverPendingConfirmationInstance {
  id: VisitInstanceId;
  startTime: LocalDateTime;
  endTime: LocalDateTime;
}

export type PublicOnboardingVisitDetails =
  | PublicOnboardingVisitDetailsTypes.Pattern
  | PublicOnboardingVisitDetailsTypes.Singles;

export interface CaregiverVisitBroadcastEngagementDetails {
  /**
   * Has the caregiver viewed this visit yet (or is it new)
   */
  viewed: boolean;
  /**
   * Has the caregiver requested this visit
   */
  requested: boolean;
  moreDetailsViewed: boolean;

  flexibleVisitTimeRequest: CaregiverFlexibleVisitRequestParams[] | null;

  /**
   * For multishift change.
   */
  requestedInstances: VisitInstanceId[] | null;
  rejectedInstances: VisitInstanceId[] | null;

  pendingConfirmationInstances?: CaregiverPendingConfirmationInstance[] | null;
  pendingConfirmationUntil?: Instant | null;
  pendingConfirmationConfirmedAt?: Instant | null;
  pendingConfirmationShifts?: CaregiverFlexibleVisitRequestParams[] | null;
}

export interface CaregiverOnboardingVisitEngagementDetails {
  /**
   * Has the caregiver viewed this visit yet (or is it new)
   */
  viewed: boolean;
  /**
   * Has the caregiver requested this visit
   */
  requested: boolean;
  moreDetailsViewed: boolean;

  /**
   * For multishift change.
   */
  requestedInstances: OnboardingVisitInstanceId[] | null;
  rejectedInstances: OnboardingVisitInstanceId[] | null;
}

export interface VisitLabelText {
  text: string;
  color: string;
}

export declare namespace PublicVisitBroadcastDetailsTypes {
  export interface FlexiblePattern extends BaseDetails {
    type: "FlexiblePattern";
    flexibleVisitParams: FlexibleVisitParams;
  }

  export interface Pattern extends BaseDetails {
    type: "Pattern";
    weeklyPattern: BroadcastPattern;
    visitInstances: {
      date: VisitDatesParams;
      visitInstanceId: VisitInstanceId;
    }[];
  }

  export interface Singles extends BaseDetails {
    type: "Singles";
    visitInstances: {
      date: VisitDatesParams;
      visitInstanceId: VisitInstanceId;
    }[];
  }

  interface BaseDetails {
    id: VisitBroadcastId;
    createdAt: Instant;
    agency: Agency;
    startTimeLocal: LocalDateTime;
    endTimeLocal: LocalDateTime;
    languages: string[];
    generalNotes: string;
    location: AddressComponents;
    price: number | null;

    /**
     * timezone from the tz database (IANA)
     */
    timezone: string;
    createdByFirstName: string;
    createdByLastName: string;
    visitLabels: VisitLabelText[];
    patientId: PatientId;
    patientFirstName: string;
    patientIsActive: boolean;
    patientAge: number | null;
    patientInitials: string;
    patientGender: PatientGender;
    patientAllergies: Allergy[];
    patientMedicalNotes: string | null;
    patientLanguages: string[];
    patientRequests: string[];
    patientTasks: string[];
    createdById: AgencyMemberId;
    certifications: CaregiverCertification[];
    hideEndDateFromCaregiver: boolean;
    staffingPreferences2: VisitStaffingPreferences;
    patientAvailability: PatientVisitTimeFramesByDay[] | null;
    allowMultipleCaregivers: boolean;
    visitInfo: string[] | null;
    nearLocationDisplay: string | null;
    preferenceMismatchReasons: BroadcastPreferenceMismatchReason[];
  }
}

/** @TJS-type string */
export type BroadcastPreferenceMismatchReason = "days_availability";

export interface CaregiverClusterDetails {
  visitIds: VisitBroadcastId[];
  clusterId: ClusterId;
  engagement: CaregiverClusterEngagementDetails;
}

export interface NotInterestedReason {
  payload: string;
}

export interface VisitLabel {
  text: string;
  color: string;
}

export interface ConfidentialVisitDetails {
  patientName: string;

  patientAddress: string;
  patientAddress2: string | null;
  patientAddressInstructions: string | null;
  patientPhone: string | null;

  patientAlternativePhone: string | null;

  caseDetails: string;
}

export interface CaregiverClusterEngagementDetails {
  /**
   * Has the caregiver requested this cluster
   */
  requested: boolean;

  moreDetailsViewed: boolean;

  viewed: boolean;
}

export interface CaregiverVisitEngagementDetails {
  /**
   * Has the caregiver viewed this visit yet (or is it new)
   */
  viewed: boolean;

  /**
   * Has the caregiver requested this visit
   */
  requested: boolean;

  flexibleVisitRequestParams: VisitDayTimeParams | null;

  splitShiftVisitRequestParams: SplitShiftVisitRequestParams | null;

  moreDetailsViewed: boolean;
}

export interface CaregiverImportedVisitDetails {
  id: ImportedVisitId;

  agencyName: string;

  patientName: string;

  timezone: string;

  startTimeUTC: Instant;
  endTimeUTC: Instant;

  startTimeLocal: LocalDateTime;
  endTimeLocal: LocalDateTime;

  agencyMemberId: AgencyMemberId | null;
}

export interface CaregiverImportedVisitDetailsTaskUnion extends CaregiverImportedVisitDetails {
  union_type: "IMPORTED_VISIT";
}

export interface AssignedCaregiver {
  id: CaregiverId;
}

export interface ClockedInStatus {
  status: ClockedInStatusDetails | null;
}

export interface ClockedInStatusDetails {
  visitInstanceId: VisitInstanceId;

  /**
   * @deprecated Use [[clockInTime]]
   */
  clockedInAt: Instant;

  clockInTime: LocalDateTime;
}

export interface VisitCancellationDetails {
  reason: string;
}

export interface VisitsPreviewedRequest {
  visitIds: VisitBroadcastId[];
  clusterIds: ClusterId[];
}

export type RNVisitParams = IRNVisitParams | null;

interface IRNVisitParams {
  /**
   * duration in minutes
   */
  duration: number;

  isFlexible: boolean;

  contactPatient?: boolean;
}

export interface RNVisitRequestParams {
  startTime: LocalDateTime;
  endTime: LocalDateTime;
}

export interface OverlappedVisitDetails {
  patient: string;
  visitStartTime: LocalDateTime;
  visitEndTime: LocalDateTime;
}

export interface VisitInstanceClockinClockoutRequestParams {
  geoLocation: Geo;
  adjustVisitTimes: boolean;
}

export enum ClockInOutCallType {
  IN = "IN",
  OUT = "OUT",
}

export enum MethodType {
  TEL = "IVR",
  MOBILE = "MOBILE",
  MANUAL = "MANUAL",
  TIME_SHEET = "TIME_SHEET",
}

export interface GeoAndAddress {
  geoLocation: Geo;
  address: string | null;
}

export interface ResceduleVisitInstanceDetails {
  dayTimeParams: VisitDayTimeParams | null;
  duration: number;
  startTimeLocal: LocalDateTime;
  endTimeLocal: LocalDateTime;
}

export interface RNVisitRequestParams {
  startTime: LocalDateTime;
  endTime: LocalDateTime;
}

export interface SplitShiftShift {
  dayOfWeek: DayOfWeek;

  /**
   * 0 or 1 (0 is morning, 1 is evening)
   */
  shiftNum: 0 | 1;
}

export interface SplitShiftParams {
  availableShifts: SplitShiftShift[];

  /**
   * Always between 0:00 and 12:00 (shifts are always 12 hours)
   */
  shiftChangeTime: LocalTime;
  minShiftsPerCaregiver: number;
  maxShiftsPerCaregiver: number;
}

export interface SplitShiftCaregiverRequest {
  shift: SplitShiftShift;
  caregiversRequests: Caregiver[];
}

export interface SplitShiftVisitRequestParams {
  splitShifts: SplitShiftShift[];
}

export interface ConvertImportedVisit {
  success: boolean;
  candidatePatientAddresses: string[];
}
export interface CaregiverRequestsVisitRequest {
  requestedInstances: VisitInstanceId[];
}

export type FlexiblePatternVisitType =
  | "PermanentFlexibleHoursAndDaysPerWeek"
  | "PermanentFlexibleHoursAndDaysPattern"
  | "PermanentFlexibleHoursFixedDaysPerWeek"
  | "PermanentFlexibleHoursFixedDaysPattern"
  | "PermanentFixedHoursFlexibleDays";

export type FlexibleVisitParams = FlexiblePermanentVisitParams | SingleFlexible;

export interface SingleFlexible {
  type: "SingleFlexible";
  date: LocalDate;
  amountOfHours: number;
  excludedDaysFromCaregivers: DayOfWeek[];
}

export type FlexiblePermanentVisitParams =
  | PermanentFlexibleHoursAndDaysPerWeek
  | PermanentFlexibleHoursAndDaysPattern
  | PermanentFlexibleHoursFixedDaysPattern
  | PermanentFlexibleHoursFixedDaysPerWeek
  | PermanentFixedHoursFlexibleDays;
export interface PermanentFlexibleHoursAndDaysPerWeek {
  type: "PermanentFlexibleHoursAndDaysPerWeek";
  excludedDaysFromCaregivers: DayOfWeek[];
  daysToChooseFrom: DayOfWeek[];
  amountOfHoursPerWeek: number;
  minHoursPerShift: number;
  maxHoursPerShift: number;
}
export interface PermanentFlexibleHoursAndDaysPattern {
  type: "PermanentFlexibleHoursAndDaysPattern";
  excludedDaysFromCaregivers: DayOfWeek[];
  daysToChooseFrom: DayOfWeek[];
  flexibleDetails: {
    amountOfDays: number;
    amountOfHours: number;
  }[];
}
export interface PermanentFlexibleHoursFixedDaysPerWeek {
  type: "PermanentFlexibleHoursFixedDaysPerWeek";
  excludedDaysFromCaregivers: DayOfWeek[];
  amountOfHoursPerWeek: number;
  days: DayOfWeek[];
  minHoursPerShift: number;
  maxHoursPerShift: number;
}
export interface PermanentFlexibleHoursFixedDaysPattern {
  type: "PermanentFlexibleHoursFixedDaysPattern";
  excludedDaysFromCaregivers: DayOfWeek[];
  details: {
    day: DayOfWeek;
    amountOfHours: number;
  }[];
}
export interface PermanentFixedHoursFlexibleDays {
  type: "PermanentFixedHoursFlexibleDays";
  excludedDaysFromCaregivers: DayOfWeek[];
  daysToChooseFrom: {
    day: DayOfWeek;
    startTime: LocalTime;
    endTime: LocalTime;
  }[];
  amountOfDays: number;
}

export interface CaregiverFlexibleVisitRequestParams {
  day: DayOfWeek;
  startTime: LocalTime;
  endTime: LocalTime;
}

export interface FlexibleRequestParamsReq {
  days: CaregiverFlexibleVisitRequestParams[];
}

export interface PatientVisitTimeFramesByDay {
  day: DayOfWeek;
  timeframes: PatientTimeFrames[];
}

export interface PatientTimeFrames {
  startTime: number;
  endTime: number;
}

export type EngagementStatus = "AVAILABLE" | "REQUESTED" | "PENDING_CONFIRMATION";

export interface ClockinClockoutRecordSelfieCreateParams {
  imageBase64: string;
}

export type ClockinClockoutCheckAdjustmentStatusResult =
  | { status: "SUCCESS"; adjustedUTC: Instant; adjustedLocal: LocalDateTime; offset: Duration }
  | { status: "ERROR"; error: "OUTSIDE_OF_OFFSET"; adjustedLocal: LocalDateTime }
  | { status: "ERROR"; error: "CANT_ADJUST_DAY"; adjustedLocal: LocalDateTime }
  | { status: "ERROR"; error: "VISIT_ALREADY_CLOCKED_IN"; adjustedLocal: LocalDateTime }
  | {
      status: "ERROR";
      error: "VISIT_BACK_TO_BACK";
      visitInstanceId: VisitInstanceId;
      adjustedLocal: LocalDateTime;
    }
  | {
      status: "ERROR";
      error: "VISIT_OVERLAPPING";
      visitInstanceId: VisitInstanceId;
      adjustedLocal: LocalDateTime;
    };

export interface ClockinClockoutCheckAdjustmentStatusResponse {
  result: ClockinClockoutCheckAdjustmentStatusResult;
}

export interface CheckIfAllDocumentsSubmittedResponse {
  allSubmitted: boolean;
}
